export interface MarkdownCommand {
  name: string;
  title?: string;
  icon?: string;
  wrap?: string;
  linePrefix?: string;
  ordered?: boolean;
  template?: string;
}

export interface MarkdownEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

const lineBoundsOf = (value: string, start: number, end: number) => {
  const from = value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end);
  const to = lineEnd === -1 ? value.length : lineEnd;
  return { from, to };
};

const applyWrap = (marker: string, value: string, start: number, end: number): MarkdownEdit => {
  const selected = value.slice(start, end);
  const before = value.slice(0, start);
  const after = value.slice(end);

  const alreadyWrapped = before.endsWith(marker) && after.startsWith(marker);
  if (alreadyWrapped) {
    return {
      value: before.slice(0, -marker.length) + selected + after.slice(marker.length),
      selectionStart: start - marker.length,
      selectionEnd: end - marker.length,
    };
  }

  return {
    value: `${before}${marker}${selected}${marker}${after}`,
    selectionStart: start + marker.length,
    selectionEnd: end + marker.length,
  };
};

const applyLinePrefix = (
  prefix: string,
  ordered: boolean,
  value: string,
  start: number,
  end: number
): MarkdownEdit => {
  const { from, to } = lineBoundsOf(value, start, end);
  const block = value.slice(from, to);
  const lines = block.split('\n');

  const marks = lines.map((_, index) => (ordered ? `${index + 1}. ` : prefix));
  const alreadyPrefixed = lines.every((line, index) => line.startsWith(marks[index]));

  const nextLines = lines.map((line, index) =>
    alreadyPrefixed ? line.slice(marks[index].length) : `${marks[index]}${line}`
  );
  const nextBlock = nextLines.join('\n');

  return {
    value: value.slice(0, from) + nextBlock + value.slice(to),
    selectionStart: from,
    selectionEnd: from + nextBlock.length,
  };
};

const applyTemplate = (template: string, value: string, start: number, end: number): MarkdownEdit => {
  const selected = value.slice(start, end) || 'texto';
  const filled = template.replace('$1', selected);
  const cursor = filled.indexOf('$2');
  const finalText = filled.replace('$2', '');

  return {
    value: value.slice(0, start) + finalText + value.slice(end),
    selectionStart: start + (cursor === -1 ? finalText.length : cursor),
    selectionEnd: start + (cursor === -1 ? finalText.length : cursor),
  };
};

export const applyMarkdownCommand = (
  command: MarkdownCommand,
  value: string,
  start: number,
  end: number
): MarkdownEdit => {
  if (command.template) return applyTemplate(command.template, value, start, end);
  if (command.linePrefix !== undefined) {
    return applyLinePrefix(command.linePrefix, Boolean(command.ordered), value, start, end);
  }
  if (command.wrap) return applyWrap(command.wrap, value, start, end);

  return { value, selectionStart: start, selectionEnd: end };
};

export const MARKDOWN_COMMANDS: MarkdownCommand[] = [
  { name: 'bold', title: 'Negrito', icon: 'B', wrap: '**' },
  { name: 'italic', title: 'Itálico', icon: 'I', wrap: '_' },
  { name: 'strikethrough', title: 'Tachado', icon: 'S', wrap: '~~' },
  { name: 'separator' },
  { name: 'unordered-list', title: 'Lista com marcadores', icon: '••', linePrefix: '- ' },
  { name: 'ordered-list', title: 'Lista numerada', icon: '1.', linePrefix: '', ordered: true },
  { name: 'separator' },
  { name: 'heading', title: 'Título', icon: 'H', linePrefix: '### ' },
  { name: 'quote', title: 'Citação', icon: '❝', linePrefix: '> ' },
  { name: 'link', title: 'Link', icon: '🔗', template: '[$1]($2url)' },
];
